export type AlphabeticalKey =
  | 'KeyA'
  | 'KeyB'
  | 'KeyC'
  | 'KeyD'
  | 'KeyE'
  | 'KeyF'
  | 'KeyG'
  | 'KeyH'
  | 'KeyI'
  | 'KeyJ'
  | 'KeyK'
  | 'KeyL'
  | 'KeyM'
  | 'KeyN'
  | 'KeyO'
  | 'KeyP'
  | 'KeyQ'
  | 'KeyR'
  | 'KeyS'
  | 'KeyT'
  | 'KeyU'
  | 'KeyV'
  | 'KeyW'
  | 'KeyX'
  | 'KeyY'
  | 'KeyZ';

export type ObservableKey =
  | AlphabeticalKey
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'Enter'
  | 'Escape'
  | 'Space'
  | 'PageUp'
  | 'PageDown'
  | 'Home'
  | 'End'
  | 'Period'
  | 'Comma'
  | 'Tab';

const alphabeticalKeys = new Map<number, AlphabeticalKey>([
  [65, 'KeyA'],
  [66, 'KeyB'],
  [67, 'KeyC'],
  [68, 'KeyD'],
  [69, 'KeyE'],
  [70, 'KeyF'],
  [71, 'KeyG'],
  [72, 'KeyH'],
  [73, 'KeyI'],
  [74, 'KeyJ'],
  [75, 'KeyK'],
  [76, 'KeyL'],
  [77, 'KeyM'],
  [78, 'KeyN'],
  [79, 'KeyO'],
  [80, 'KeyP'],
  [81, 'KeyQ'],
  [82, 'KeyR'],
  [83, 'KeyS'],
  [84, 'KeyT'],
  [85, 'KeyU'],
  [86, 'KeyV'],
  [87, 'KeyW'],
  [88, 'KeyX'],
  [89, 'KeyY'],
  [90, 'KeyZ']
]);

const observableKeys = new Map<number, ObservableKey>([
  [9, 'Tab'],
  [13, 'Enter'],
  [27, 'Escape'],
  [32, 'Space'],
  [33, 'PageUp'],
  [34, 'PageDown'],
  [35, 'End'],
  [36, 'Home'],
  [37, 'ArrowLeft'],
  [38, 'ArrowUp'],
  [39, 'ArrowRight'],
  [40, 'ArrowDown'],
  [74, 'KeyJ'],
  [75, 'KeyK'],
  [190, 'Period'],
  [188, 'Comma'],
  ...alphabeticalKeys
]);

export const allAlphabeticalKeys: AlphabeticalKey[] = Array.from(alphabeticalKeys).map(
  ([, key]) => key
);

export const allObservableKeys: ObservableKey[] = Array.from(observableKeys).map(([, key]) => key);

export const wasKeyPressed = (
  key: ObservableKey,
  ev: KeyboardEvent | React.KeyboardEvent
): boolean => mapKeyToNormalized(ev.keyCode, 'code' in ev ? ev.code : undefined) === key;

/**
 * converts a `keyCode`/`code` combo to a normalized `code`-compliant string
 * (@see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key). `keyCode` is supported on all major
 * browsers at the moment, but is marked as deprecated already and scheduled for removal. This logic aims at
 * evaluating the `keyCode` first, as it is the cross-browser solution at the moment, and if that field has already
 * been removed per deprecation, it checks the `code` field.
 *
 * if neither of the approaches can find a valid mapping, they both return `undefined`.
 */
const mapKeyToNormalized = (keyCode?: number, code?: string): ObservableKey | undefined => {
  if (keyCode) {
    return observableKeys.get(keyCode);
  }

  return Array.from(observableKeys.values()).find(value => value === code);
};
